import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { apiEndpoints } from 'Utils/ApiEndpoints';

const baseUrl = process.env.REACT_APP_BASE_URL
const baseQueryWithReauth = async (args, api, extraOptions) => {
    let result = await fetchBaseQuery({
        baseUrl: baseUrl,
        prepareHeaders: (headers, { getState }) => {
            const token = getState().auth?.authData?.token;
            if (token) {
                headers.set("Authorization", `Bearer ${token}`);
            }
            return headers;
        },
    })(args, api, extraOptions);

    if (result?.error && result?.error.status === 401) {
        window.location.href = '/'
    }
    return result;
};

export const userApi = createApi({
    reducerPath: 'userApi',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['User'],
    endpoints: (builder) => ({
        getUserData: builder.query({
            // Update the query function to accept all possible parameters
            query: ({ page, limit, search_key, sort_column, sort_direction }) => {
                // Use a params object to cleanly handle optional parameters
                const params = {
                    page,
                    limit,
                };

                // Conditionally add parameters if they exist
                if (search_key) {
                    params.search_key = search_key;
                }
                if (sort_column) {
                    params.sort_column = sort_column;
                }
                if (sort_direction) {
                    params.sort_direction = sort_direction;
                }

                return {
                    url: apiEndpoints?.userGetUrl,
                    method: 'GET',
                    params: params, // RTK Query will automatically convert this to a query string
                };
            },
            providesTags: ['User'],
        }),
        getTrackingData: builder.query({
            query: ({ page, limit, searchData }) => ({
                url: `${apiEndpoints?.getTrackingUrl}?page=${page}&limit=${limit}&search_key=${searchData ?? ''}`,
                method: 'GET',
            }),
            providesTags: ['User'],
        }),
        getUserDataDetails: builder.query({
            query: (userId) => ({
                url: `${apiEndpoints?.userDetailsUrl}?user_id=${userId}`,
                method: 'GET',
            }),
            providesTags: ['User'],
        }),
        getTrackingDataDetails: builder.query({
            query: (tracking_id) => ({
                url: `${apiEndpoints?.getTrackingDataDetails}?tracking_id=${tracking_id}`,
                method: 'GET',
            }),
            providesTags: ['User'],
        }),

        updateUserStatus: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.userStatusUrl,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ['User'],
        }),
        updateProductStatus: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.updatTrackingStatusUrl,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ['User'],
        }),
        getDashboardData: builder.query({
            query: () => ({
                url: `${apiEndpoints?.userDashboardUrl}`,
                method: 'GET',
            }),
        }),
        updateProductDelete: builder.mutation({
            query: (data) => ({
                url: apiEndpoints?.updateProductDelete,
                method: "DELETE",
                body: data
            }),
            invalidatesTags: ['User'],
        }),
        addExportUserData: builder.mutation({
            query: (commonData) => ({
                url: apiEndpoints?.exportUsers,
                method: "POST",
                body: commonData
            }),
            invalidatesTags: ['User'],
        }),
    })
});

export const {
    useGetUserDataQuery,
    useGetUserDataDetailsQuery,
    useUpdateUserStatusMutation,
    useAddExportUserDataMutation,
    useGetDashboardDataQuery,
    useGetTrackingDataQuery,
    useUpdateProductStatusMutation,
    useGetTrackingDataDetailsQuery,
    useUpdateProductDeleteMutation
} = userApi;
